import React, { useEffect, useRef, useState } from 'react';
import { content, SupportedLocale } from '../lib/content';
import { ContactMap } from './ContactMap';

type ContactSectionProps = {
  locale?: SupportedLocale;
};

// Formspree endpoint (Twój)
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xgovknwo';

export const ContactSection: React.FC<ContactSectionProps> = ({ locale = 'pl' }) => {
  const t = content[locale].contact;

  const formRef = useRef<HTMLFormElement | null>(null);
  const helperRef = useRef<HTMLParagraphElement | null>(null);

  const [mapHeight, setMapHeight] = useState<number | undefined>(undefined);
  const [mapOffset, setMapOffset] = useState<number | undefined>(undefined);
  const [mapWidth, setMapWidth] = useState<number | undefined>(undefined);

  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSending) return;

    setIsSending(true);
    setStatus('idle');

    try {
      const form = event.currentTarget;
      const formData = new FormData(form);

      // Opcjonalnie: temat maila w Formspree
      formData.append('_subject', 'Wiadomość ze strony pienkowskilegal.pl');
      formData.append('_locale', locale);

      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    const updateMapHeight = () => {
      if (window.innerWidth < 992) {
        setMapHeight(undefined);
        setMapOffset(undefined);
        setMapWidth(undefined);
        return;
      }

      const formEl = formRef.current;
      if (!formEl) return;

      const sectionEl = formEl.closest('.contact-section') as HTMLElement | null;
      if (!sectionEl) return;

      const canvasEl = sectionEl.querySelector('.contact-map__canvas') as HTMLElement | null;
      if (!canvasEl) return;

      const sectionRect = sectionEl.getBoundingClientRect();
      const formRect = formEl.getBoundingClientRect();
      const canvasRect = canvasEl.getBoundingClientRect();

      const bottomOfForm = formRect.bottom - sectionRect.top;
      const topOfCanvas = canvasRect.top - sectionRect.top;
      const baseHeight = bottomOfForm - topOfCanvas;

      if (baseHeight <= 0) {
        setMapHeight(undefined);
        setMapOffset(undefined);
        setMapWidth(undefined);
        return;
      }

      const extra = baseHeight * 0.3;

      const helperEl = helperRef.current;
      if (helperEl) {
        const helperRect = helperEl.getBoundingClientRect();
        setMapWidth(helperRect.width * 0.85);
      } else {
        setMapWidth(undefined);
      }

      setMapHeight(baseHeight + extra);
      setMapOffset(extra);
    };

    updateMapHeight();
    window.addEventListener('resize', updateMapHeight);
    return () => window.removeEventListener('resize', updateMapHeight);
  }, [locale]);

  const sendingLabel =
    locale === 'pl' ? 'Wysyłanie…' : locale === 'en' ? 'Sending…' : 'Enviando…';

  const successMsg =
    locale === 'pl'
      ? 'Wiadomość wysłana. Dziękuję!'
      : locale === 'en'
      ? 'Message sent. Thank you!'
      : 'Mensaje enviado. ¡Gracias!';

  const errorMsg =
    locale === 'pl'
      ? 'Nie udało się wysłać wiadomości. Spróbuj ponownie lub napisz na biuro@pienkowskilegal.pl'
      : locale === 'en'
      ? 'Message could not be sent. Please try again or email biuro@pienkowskilegal.pl'
      : 'No se pudo enviar el mensaje. Inténtalo de nuevo o escribe a biuro@pienkowskilegal.pl';

  return (
    <section id={t.id} className="contact-section reveal">
      <div className="container">
        <div className="contact-grid">
          <div className="contact-info">
            <div className="section-heading">
              <h2>{t.title}</h2>
            </div>

      
            <ul className="contact-list">
              {t.details.map((detail) => (
                <li key={detail.label}>
                  <span className="contact-label">{detail.label}: </span>
                  {detail.href ? (
                    <a href={detail.href} className="contact-link">
                      {detail.value}
                    </a>
                  ) : (
                    <span>{detail.value}</span>
                  )}
                </li>
              ))}
            </ul>

            <p className="muted" ref={helperRef}>
              {t.helper}
            </p>

            <ContactMap height={mapHeight} offset={mapOffset} width={mapWidth} />
          </div>

          <form className="contact-form" onSubmit={handleSubmit} ref={formRef}>
            {t.fields.map((field) => {
              const inputProps = {
                name: field.name,
                id: field.name,
                required: field.required,
              };

              if (field.name === 'message') {
                return (
                  <label key={field.name} className="form-field" htmlFor={field.name}>
                    <span>{field.label}</span>
                    <textarea {...inputProps} rows={4} />
                  </label>
                );
              }

              return (
                <label key={field.name} className="form-field" htmlFor={field.name}>
                  <span>{field.label}</span>
                  <input type={field.type ?? 'text'} {...inputProps} />
                </label>
              );
            })}

            {/* honeypot antyspam */}
            <input
              type="text"
              name="_gotcha"
              tabIndex={-1}
              autoComplete="off"
              style={{ display: 'none' }}
            />

            {/* CONSENT – checkbox + link otwierający modal */}
            <label className="form-field consent">
              <input type="checkbox" required name="consent" />
              <span>
                {t.consentLabel}{' '}
                <a
                  href=""
                  onClick={(e) => {
                    e.preventDefault();
                    window.dispatchEvent(new Event('legal:privacy'));
                  }}
                >
                  {/* fallback gdybyś chwilowo nie miał pola w którymś języku */}
                  {'consentLinkLabel' in t ? (t as any).consentLinkLabel : 'polityką prywatności'}
                </a>
              </span>
            </label>

            <button className="btn btn-primary" type="submit" disabled={isSending}>
              {isSending ? sendingLabel : t.submitLabel}
            </button>

            {status === 'success' && (
              <p className="muted" style={{ marginTop: 12 }}>
                {successMsg}
              </p>
            )}

            {status === 'error' && (
              <p className="muted" style={{ marginTop: 12 }}>
                {errorMsg}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};
